import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import {
  WORK_STATUS,
  N_OF_MEDIAN_TIME,
  INCLUDED_COUNTRIES_FOR_WORKSHIFT,
  EXCLUDED_DESIGNATION_FOR_WORKSHIFT,
} from 'constants/dashboard';
import { SELF } from 'constants/fetchTypes';
import { REMOTE } from 'constants/appConstants';
import { mixPanelEvents } from 'constants/mixPanel';
import { ATTENDANCE_URL } from 'constants/endpoints';

import * as mixpanel from 'services/mixPanel';
import * as attendanceService from 'services/attendance';

import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { convertTimeToAMPM, getMedianTime } from 'utils/time';
import { subtractDays, getFormattedDate, getCurrentWeekRange, filterDataByDateRange } from 'utils/date';

import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';
import { useCurrentUser } from 'hooks/query/useUserInfo';
import { useSelfAttendance } from 'contexts/SelfAttendanceContext';

import ClockInTime from './components/ClockInTime';
import WorkStatusCount from './components/WorkStatusCount';
import { ClockInHistoryModal } from './components/clockInHistory/ClockInHistoryModal';
import { ClockInHistoryButton } from './components/clockInHistory/ClockInHistoryButton';
import { getWorkStatusSummaryData, getWorklogTotalDuration } from 'utils/leave';
import type { WorkStatusSummary } from 'interface/leave';

type User = {
  id?: string;
  country?: string;
  designation?: {
    name?: string;
  };
};

type FiscalYear = {
  startDate: string;
};

type CalendarDay = {
  date: string;
  checkinTime?: string | null;
};

type UnfreezeRequest = {
  date?: string;
  unfreezeFrom?: string;
  unfreezeTo?: string;
  updatedAt?: string;
  isUnfrozen?: boolean;
};

type UnfreezeRequestSummaryInput = {
  unfreezeFrom: string;
  unfreezeTo: string;
  updatedAt: string;
  isUnfrozen: boolean;
};

export default function AllocationCardFooter() {
  const user = useCurrentUser() as User;

  const shouldShowWorkshift =
    INCLUDED_COUNTRIES_FOR_WORKSHIFT.includes(user?.country) &&
    !EXCLUDED_DESIGNATION_FOR_WORKSHIFT.includes(user?.designation?.name);

  const [totalDuration, setTotalDuration] = useState<string | number>(0);

  const [medianTime, setMedianTime] = useState<string | null>(null);
  const [clockinHistory, setClockinHistory] = useState<CalendarDay[]>([]);

  const [isCalendarDataLoading, setIsCalendarDataLoading] = useState(false);

  const [workStatusSummaryData, setWorkStatusSummaryData] = useState<WorkStatusSummary>({ wfh: 0, wfo: 0, leave: 0 });

  const [isSelfAttendanceLoading, setIsSelfAttendanceLoading] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const [unfreezeRequests, setUnfreezeRequests] = useState<UnfreezeRequest[]>([]);

  const currentFiscalYear = useCurrentFiscalYear() as FiscalYear;

  const {
    selfAttendance: { missing: selfMissing, pending: selfPending },
    setSelfAttendance,
  } = useSelfAttendance();

  const { self } = mixPanelEvents.view;

  const isPendingZero = selfPending === 0;
  const isMissingZero = Array.isArray(selfMissing);

  const getCalendarMedianTime = (calendarData: CalendarDay[]) => {
    const times = calendarData
      .map(({ checkinTime }) => checkinTime)
      .filter((checkinTime): checkinTime is string => Boolean(checkinTime));

    return getMedianTime(times);
  };
  useEffect(() => {
    if (isObjectEmpty(currentFiscalYear)) {
      return;
    }

    const endDate = getFormattedDate();
    const startDate = getFormattedDate(currentFiscalYear.startDate);

    (async () => {
      const filters = {
        fetchType: SELF,
        startDate,
        endDate,
        fields: 'worklog,missing',
      };

      try {
        setIsSelfAttendanceLoading(true);

        const { data: selfData } = (await attendanceService.fetchSummary(filters)) as {
          data: { missing?: number; worklog?: { pending?: number } };
        };

        setSelfAttendance({ missing: selfData.missing || 0, pending: selfData.worklog.pending || 0 });
      } catch (err) {
        handleError(err);
      } finally {
        setIsSelfAttendanceLoading(false);
      }
    })();
  }, [currentFiscalYear, setSelfAttendance]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const fetchCalendarData = async () => {
      const currentDate = getFormattedDate();
      const calendarDataSize = 40;

      const [weekStartDate, weekEndDate] = getCurrentWeekRange(currentDate);
      const medianStartDate = subtractDays(currentDate, N_OF_MEDIAN_TIME);

      const params = {
        fetchType: SELF,
        startDate: medianStartDate,
        endDate: weekEndDate,
        withCheckinTime: true,
        size: calendarDataSize,
      };

      try {
        setIsCalendarDataLoading(true);

        const [data, unfreezeRequestsData] = (await Promise.all([
          attendanceService.fetch(params),
          attendanceService.fetchUnfreezeRequestsByUserId(user.id),
        ])) as [CalendarDay[], UnfreezeRequest[]];

        const weeklyData = filterDataByDateRange({
          data,
          key: 'date',
          startDate: weekStartDate,
          endDate: weekEndDate,
        }) as CalendarDay[];
        const medianData = filterDataByDateRange({
          data,
          key: 'date',
          startDate: medianStartDate,
          endDate: currentDate,
        }) as CalendarDay[];

        const medianTime = getCalendarMedianTime(medianData);
        const totalDuration = getWorklogTotalDuration(weeklyData);
        const unfreezeRequestsSummaryData = unfreezeRequestsData.filter(
          (request): request is UnfreezeRequestSummaryInput =>
            Boolean(request.unfreezeFrom && request.unfreezeTo && request.updatedAt) &&
            typeof request.isUnfrozen === 'boolean'
        );
        const workStatusSummary = getWorkStatusSummaryData(medianData, unfreezeRequestsSummaryData);

        const formattedMedianTime = medianTime && convertTimeToAMPM(medianTime);

        setUnfreezeRequests(unfreezeRequestsData);
        setClockinHistory(medianData);
        setTotalDuration(totalDuration);
        setMedianTime(formattedMedianTime);
        setWorkStatusSummaryData(workStatusSummary);
      } catch (error) {
        handleError(error);
      } finally {
        setIsCalendarDataLoading(false);
      }
    };

    void fetchCalendarData();
  }, [user?.id]);

  const WORK_STATUS_MAPPING: Record<keyof WorkStatusSummary, string> = {
    wfo: WORK_STATUS.WFO,
    wfh: REMOTE,
    leave: WORK_STATUS.LEAVE,
  };

  const isLoading = isCalendarDataLoading || isSelfAttendanceLoading;

  if (isLoading) {
    return (
      <>
        <SkeletonTheme baseColor="#fdfdfd" highlightColor="#f0f3f5">
          <Skeleton className="weekly-record-card-loader wp-100" height={62} />
          {shouldShowWorkshift && <Skeleton className="median-office-time-card-loader wp-100" height={86} />}
        </SkeletonTheme>
      </>
    );
  }

  return (
    <>
      <div className="d-flex actions flex-direction-column">
        <div className="weekly-action d-flex">
          <div className="actions__container">
            <div className="actions__footer justify-content-center">
              <div className="footer__log-content">
                <div className="footer__top">{totalDuration}</div>
                <div>
                  <p className="footer__bottom-text">logged this week</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pending-action">
            <a
              className={classNames(
                'pending-action__item',
                { 'pending-action__item--empty': isPendingZero },
                { 'pending-action__item--pending': !isPendingZero }
              )}
              href={ATTENDANCE_URL}
              target={'_blank'}
              onClick={() => {
                mixpanel.trackEvent(self.worklogPending);
              }}
              rel="noreferrer"
            >
              {selfPending}
              <div className="pending-action__title">Worklog Pending</div>
            </a>
            <a
              className={classNames(
                'pending-action__item',
                { 'pending-action__item--empty': isMissingZero },
                { 'pending-action__item--missed': !isMissingZero }
              )}
              href={ATTENDANCE_URL}
              target="_blank"
              onClick={() => {
                mixpanel.trackEvent(self.attendanceMissed);
              }}
              rel="noreferrer"
            >
              <div>{isMissingZero ? 0 : selfMissing}</div>
              <div className="pending-action__title">Attendance Missed</div>
            </a>
          </div>
        </div>
      </div>

      {shouldShowWorkshift && (
        <div className="actions median-office-time px-16 pbpx-12">
          <ClockInHistoryModal
            TriggerBtn={() => <ClockInHistoryButton setIsHistoryModalOpen={setIsHistoryModalOpen} />}
            isOpen={isHistoryModalOpen}
            onClose={() => setIsHistoryModalOpen(false)}
            clockinHistoryData={clockinHistory}
            isClockinHistoryLoading={isCalendarDataLoading}
            medianClockInTime={medianTime}
            unfreezeRequests={unfreezeRequests}
          />
          <div className="median-office-time__main d-flex justify-content-between align-items-center">
            <ClockInTime medianClockInTime={medianTime} />

            {(Object.entries(workStatusSummaryData) as Array<[keyof WorkStatusSummary, number]>).map(
              ([status, count], index) => {
                const workStatus = WORK_STATUS_MAPPING[status];
                const isLastChild = index === Object.entries(workStatusSummaryData).length - 1;

                return (
                  <WorkStatusCount
                    key={status}
                    status={workStatus}
                    count={count}
                    className={!isLastChild ? 'border-right-grey-15' : ''}
                  />
                );
              }
            )}
          </div>
        </div>
      )}
    </>
  );
}
